/**
 * HVP Cascade Controller - selection state
 *
 * Above/below slices of the selection set, completeness, reset and the
 * public state/progress/Smart-Cascade getters.
 *
 * Split out of cascade-controller.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPCascadeController.prototype by
 * cascade-controller.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const CascadeStateMethods = {
    /**
     * Get selections from attributes ABOVE the given index
     *
     * Returns only selections from indexes 0 to index-1 in the attribute order.
     * Used by Smart Cascade to filter options based on upstream selections only.
     *
     * @param {number} index - Current attribute index
     * @returns {Object} Object containing only selections above the index
     *
     * @example
     * // With attributeOrder: ['pa_size', 'pa_color', 'pa_material']
     * // And selections: { pa_size: 'large', pa_color: 'red', pa_material: 'cotton' }
     * getSelectionsAbove(2) // Returns { pa_size: 'large', pa_color: 'red' }
     * getSelectionsAbove(1) // Returns { pa_size: 'large' }
     * getSelectionsAbove(0) // Returns {}
     */
    getSelectionsAbove(index) {
        const selectionsAbove = {};

        for (let i = 0; i < index && i < this.attributeOrder.length; i++) {
            const attr = this.attributeOrder[i];
            const normalized = this.normalizeAttributeName(attr);

            if (this.selections[normalized]) {
                selectionsAbove[normalized] = this.selections[normalized];
            }
        }

        return selectionsAbove;
    },

    /**
     * Get selections from attributes BELOW the given index
     *
     * Returns only selections from indexes index+1 to end in the attribute order.
     * Used to determine if there are downstream selections that need compatibility checking.
     *
     * @param {number} index - Current attribute index
     * @returns {Object} Object containing only selections below the index
     *
     * @example
     * // With attributeOrder: ['pa_size', 'pa_color', 'pa_material']
     * // And selections: { pa_size: 'large', pa_color: 'red', pa_material: 'cotton' }
     * getSelectionsBelow(0) // Returns { pa_color: 'red', pa_material: 'cotton' }
     * getSelectionsBelow(1) // Returns { pa_material: 'cotton' }
     * getSelectionsBelow(2) // Returns {}
     */
    getSelectionsBelow(index) {
        const selectionsBelow = {};

        for (let i = index + 1; i < this.attributeOrder.length; i++) {
            const attr = this.attributeOrder[i];
            const normalized = this.normalizeAttributeName(attr);

            if (this.selections[normalized]) {
                selectionsBelow[normalized] = this.selections[normalized];
            }
        }

        return selectionsBelow;
    },

    /**
     * Check if all selections are complete
     */
    areAllSelectionsComplete() {
        for (const attr of this.attributeOrder) {
            const normalized = this.normalizeAttributeName(attr);
            if (!this.selections[normalized]) {
                return false;
            }
        }
        return true;
    },

    /**
     * Get current state
     */
    getState() {
        return {
            productId: this.productId,
            selections: { ...this.selections },
            currentVariation: this.currentVariation,
            isLoading: this.isLoading,
            initialized: this.initialized
        };
    },

    /**
     * Handle reset
     */
    handleReset(event) {
        if (event) {
            event.preventDefault();
        }

        this.selections = {};
        this.currentVariation = null;
        this.cache.clear();

        // Reset all dropdowns
        this.attributeOrder.forEach((attr, index) => {
            const normalized = this.normalizeAttributeName(attr);
            const dropdown = this.getDropdown(normalized);

            if (dropdown) {
                dropdown.value = '';

                if (index > 0) {
                    dropdown.disabled = true;
                    dropdown.classList.add('hvp-empty');
                }

                dropdown.classList.remove('hvp-single-option', 'hvp-auto-selected');
            }
        });

        this.clearVariation();
        this.loadPrefetchedOptions();

        this.emit('reset', {});
        this.fireCustomEvent('hvp_reset', {});

        this.log('Form reset');
    },

    /**
     * Get progress information
     *
     * @returns {Object} Progress object with completed, total, percentage
     */
    getProgress() {
        return this.uiManager.updateProgressIndicator(this.dropdowns);
    },

    /**
     * Enable or disable Smart Cascade feature
     *
     * @param {boolean} enabled - Whether to enable Smart Cascade
     */
    setSmartCascadeEnabled(enabled) {
        this.smartCascadeEnabled = enabled;
        this.log('Smart Cascade:', enabled ? 'enabled' : 'disabled');

        this.emit('smartCascadeToggled', {
            enabled: enabled
        });
    },

    /**
     * Get Smart Cascade status
     *
     * @returns {Object} Smart Cascade status
     */
    getSmartCascadeStatus() {
        return {
            enabled: this.smartCascadeEnabled,
            focusLoadingAttribute: this.focusLoadingAttribute,
            selectionsCount: Object.keys(this.selections).length,
            attributeCount: this.attributeOrder.length
        };
    },
};

export default CascadeStateMethods;
